import { Card, CardContent, CardHeader } from "@/components/ui/card";
import type { ParsedParam } from "@/lib/mcp/schema";
import { MessageSquare } from "lucide-react";
import { ExecutionResults } from "../shared/components";
import { PromptParameterForm } from "./PromptParameterForm";
import { usePromptExecution } from "./usePromptExecution";

export interface PromptExecutionPanelProps {
  projectId: string;
  promptName: string | null;
  parsedParams: ParsedParam[];
  configureSectionState: "pending" | "active" | "completed" | "invalidated";
}

export function PromptExecutionPanel({
  projectId,
  promptName,
  parsedParams,
  configureSectionState,
}: PromptExecutionPanelProps) {
  const { isLoading, result, error, executePrompt, canExecute } =
    usePromptExecution({
      projectId,
      promptName,
      parsedParams,
    });

  if (!promptName) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Select a prompt above to configure arguments and execute</p>
      </div>
    );
  }

  const showParametersChanged =
    configureSectionState === "invalidated" && !!(result || error);

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg font-medium">Arguments</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <PromptParameterForm
            promptName={promptName}
            parsedParams={parsedParams}
            onExecute={executePrompt}
            isExecuting={isLoading}
            canExecute={canExecute}
            showParametersChanged={showParametersChanged}
          />

          <ExecutionResults
            isLoading={isLoading}
            result={result}
            error={error}
          />
        </CardContent>
      </Card>
    </>
  );
}
